import React, { useState, useEffect, useMemo } from 'react';
import { crmService } from '../../services/crmCRUD';
import api from '../../services/api';
import { RichTextEditor } from '../common/RichTextEditor';
import { useAuth } from '../../state/auth/AuthProvider';
import Select from 'react-select';

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  campaignId?: string; // Optional: if provided, we're in edit mode
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content_html: string;
  content_text?: string;
}

interface Contact {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company_name?: string;
}

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'DEPRECATED';
}

export const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  campaignId
}) => {
  const { user } = useAuth();
  const isEditMode = !!campaignId;
  
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [toRecipients, setToRecipients] = useState<string[]>([]);
  const [ccRecipients, setCcRecipients] = useState<string[]>([]);
  const [bccRecipients, setBccRecipients] = useState<string[]>([]);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [timezone, setTimezone] = useState<string>('UTC');
  
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create combined options for autocomplete (contacts + users)
  const recipientOptions = useMemo(() => {
    const options: Array<{ value: string; label: string; email: string; type: 'contact' | 'user' }> = [];
    
    // Add contacts
    contacts.forEach(contact => {
      if (contact.email) {
        options.push({
          value: contact.id,
          label: `${contact.first_name} ${contact.last_name} (${contact.email})`,
          email: contact.email,
          type: 'contact'
        });
      }
    });
    
    // Add users
    users.forEach(user => {
      if (user.email) {
        options.push({
          value: `user:${user.id}`,
          label: `${user.first_name || ''} ${user.last_name || ''} (${user.email})`.trim(),
          email: user.email,
          type: 'user'
        });
      }
    });
    
    return options;
  }, [contacts, users]);

  // Load templates, contacts, and users on mount, and campaign data if editing
  useEffect(() => {
    if (isOpen) {
      // Reset form state when modal opens
      setCampaignName('');
      setSubject('');
      setEmailContent('');
      setSelectedTemplate('');
      setToRecipients([]);
      setCcRecipients([]);
      setBccRecipients([]);
      setError(null);
      
      // Set default scheduled date/time to 15 minutes from now (only for new campaigns, not editing)
      if (!isEditMode || !campaignId) {
        const now = new Date();
        const fifteenMinutesLater = new Date(now.getTime() + 15 * 60 * 1000); // Add 15 minutes
        
        // Format date as YYYY-MM-DD
        const dateStr = fifteenMinutesLater.toISOString().split('T')[0];
        
        // Format time as HH:MM (24-hour format)
        const hours = String(fifteenMinutesLater.getHours()).padStart(2, '0');
        const minutes = String(fifteenMinutesLater.getMinutes()).padStart(2, '0');
        const timeStr = `${hours}:${minutes}`;
        
        setScheduledDate(dateStr);
        setScheduledTime(timeStr);
      } else {
        // Reset for edit mode - will be loaded from campaign data
        setScheduledDate('');
        setScheduledTime('');
      }
      
      // Load data first, then campaign data (so recipients can be matched)
      const loadAndEdit = async () => {
        await loadData();
        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 200));
        if (isEditMode && campaignId) {
          await loadCampaignData(campaignId);
        }
      };
      loadAndEdit();
      loadUserTimezone();
    }
  }, [isOpen, campaignId, user]);

  // Load user timezone from profile
  const loadUserTimezone = async () => {
    try {
      const response = await api.get('/users/profile');
      const userProfile = response.data.data;
      const userTimezone = userProfile?.preferences?.timezone || 'UTC';
      setTimezone(userTimezone);
    } catch (err) {
      console.error('Error loading user timezone:', err);
      // Default to UTC if profile load fails
      setTimezone('UTC');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesData, contactsResponse, usersData] = await Promise.all([
        crmService.getEmailTemplates(),
        crmService.getContacts({ email_opt_out: false }), // Only show contacts who haven't unsubscribed (no CRM status filter)
        // Get users from admin endpoint (requires permissions) or use empty array if fails
        // Note: This endpoint requires user-management screen access
        api.get('/users/admin/users').then(res => res.data.data || res.data || []).catch(() => [])
      ]);
      
      // Extract contacts array from response
      // Backend returns: { success: true, data: contacts[], pagination: {...} }
      const contactsData = Array.isArray(contactsResponse) 
        ? contactsResponse 
        : (contactsResponse?.data || []);
      
      // Filter users to only show active users (status = 'ACTIVE')
      const activeUsers = Array.isArray(usersData) 
        ? usersData.filter((user: User) => user.status === 'ACTIVE')
        : [];
      
      setTemplates(Array.isArray(templatesData) ? templatesData : []);
      setContacts(Array.isArray(contactsData) ? contactsData : []);
      setUsers(activeUsers);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
      // Ensure arrays are set even on error
      setTemplates([]);
      setContacts([]);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignData = async (id: string) => {
    try {
      setLoading(true);
      // Load campaign details
      const campaign = await crmService.getEmailCampaignById(id);
      
      setCampaignName(campaign.name || '');
      setSubject(campaign.subject || '');
      setEmailContent(campaign.email_content || '');
      setSelectedTemplate(campaign.template_id || '');
      
      // Parse scheduled date/time if exists
      if (campaign.scheduled_date) {
        const scheduled = new Date(campaign.scheduled_date);
        setScheduledDate(scheduled.toISOString().split('T')[0]);
        setScheduledTime(scheduled.toTimeString().slice(0, 5)); // HH:MM format
      }
      
      // Load timezone if available
      if (campaign.scheduled_timezone) {
        setTimezone(campaign.scheduled_timezone);
      }
      
      // Always reload contacts and users to ensure we have the latest data
      // This ensures we can match recipients even if the state hasn't updated yet
      const [contactsResponse, usersData] = await Promise.all([
        crmService.getContacts({ email_opt_out: false }), // Only show contacts who haven't unsubscribed (no CRM status filter)
        api.get('/users/admin/users').then(res => res.data.data || res.data || []).catch(() => [])
      ]);
      
      const contactsData = Array.isArray(contactsResponse) 
        ? contactsResponse 
        : (contactsResponse?.data || []);
      
      const currentContacts = Array.isArray(contactsData) ? contactsData : [];
      // Filter users to only show active users (status = 'ACTIVE')
      const currentUsers = Array.isArray(usersData) 
        ? usersData.filter((user: User) => user.status === 'ACTIVE')
        : [];
      
      // Update state for future use
      setContacts(currentContacts);
      setUsers(currentUsers);
      
      // Load campaign recipients
      const recipients = await crmService.getCampaignRecipients(id);
      
      console.log('Loaded recipients:', recipients);
      console.log('Current contacts:', currentContacts.length);
      console.log('Current users:', currentUsers.length);
      
      // Separate recipients by type
      const toIds: string[] = [];
      const ccIds: string[] = [];
      const bccIds: string[] = [];
      
      for (const recipient of recipients) {
        // Use type assertion to access optional fields
        const recipientType = (recipient as any).recipient_type || 'to';
        const emailAddress = (recipient as any).email_address;
        
        if (recipient.contact_id) {
          // It's a contact - verify it exists in our contacts list
          const contact = currentContacts.find(c => c.id === recipient.contact_id);
          if (contact) {
            if (recipientType === 'to') toIds.push(recipient.contact_id);
            else if (recipientType === 'cc') ccIds.push(recipient.contact_id);
            else if (recipientType === 'bcc') bccIds.push(recipient.contact_id);
          } else {
            console.warn(`Contact ${recipient.contact_id} not found in current contacts list`);
          }
        } else if (emailAddress) {
          // It's a user (no contact_id) - find user by email
          const user = currentUsers.find((u: User) => u.email === emailAddress);
          if (user) {
            const userKey = `user:${user.id}`;
            if (recipientType === 'to') toIds.push(userKey);
            else if (recipientType === 'cc') ccIds.push(userKey);
            else if (recipientType === 'bcc') bccIds.push(userKey);
          } else {
            // User not found - might be from another tenant or deleted
            // Try to find by email in contacts as fallback
            const contactByEmail = currentContacts.find(c => c.email === emailAddress);
            if (contactByEmail) {
              // Use contact ID instead
              if (recipientType === 'to') toIds.push(contactByEmail.id);
              else if (recipientType === 'cc') ccIds.push(contactByEmail.id);
              else if (recipientType === 'bcc') bccIds.push(contactByEmail.id);
            } else {
              console.warn(`User/Contact with email ${emailAddress} not found for recipient ${recipient.id}`);
            }
          }
        }
      }
      
      console.log('Setting recipients - TO:', toIds, 'CC:', ccIds, 'BCC:', bccIds);
      
      setToRecipients(toIds);
      setCcRecipients(ccIds);
      setBccRecipients(bccIds);
    } catch (err: any) {
      console.error('Error loading campaign data:', err);
      setError(err.message || 'Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  };

  // Handle template selection - copy content
  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject || '');
      setEmailContent(template.content_html || template.content_text || '');
    }
  };

  // Handle recipient selection change (for react-select)
  const handleRecipientSelectChange = (
    type: 'to' | 'cc' | 'bcc',
    selectedOptions: any
  ) => {
    const selectedValues = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
    const setter = type === 'to' ? setToRecipients : type === 'cc' ? setCcRecipients : setBccRecipients;
    setter(selectedValues);
  };

  // Get selected options for react-select
  const getSelectedRecipients = (type: 'to' | 'cc' | 'bcc') => {
    const current = type === 'to' ? toRecipients : type === 'cc' ? ccRecipients : bccRecipients;
    return recipientOptions.filter(opt => current.includes(opt.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!campaignName || !subject || !emailContent) {
      setError('Please fill in all required fields');
      return;
    }

    if (toRecipients.length === 0 && ccRecipients.length === 0 && bccRecipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }

    try {
      setLoading(true);

      // Combine scheduled date and time with timezone
      // Send the date/time as-is (naive timestamp) along with the timezone
      // The backend will interpret this as "this time in this timezone" and convert to UTC
      let scheduledDateTime: string | undefined;
      if (scheduledDate && scheduledTime) {
        // Create naive timestamp string (no timezone info)
        // This represents the time the user entered in their selected timezone
        // Backend will interpret: "2025-01-05T14:30:00" in "America/New_York" = 2:30 PM EST
        const localDateTime = `${scheduledDate}T${scheduledTime}:00`;
        scheduledDateTime = localDateTime;
      } else if (scheduledDate) {
        scheduledDateTime = `${scheduledDate}T00:00:00`;
      }

      // Create campaign
      const campaignData: any = {
        name: campaignName,
        subject: subject,
        email_content: emailContent,
        template_id: selectedTemplate || null,
        status: scheduledDateTime ? 'scheduled' : 'draft',
        scheduled_date: scheduledDateTime || null,
        scheduled_timezone: timezone, // Store timezone for scheduling
        total_recipients: toRecipients.length + ccRecipients.length + bccRecipients.length
      };

      // Determine the campaign ID to use for recipients
      let finalCampaignId: string;
      
      if (isEditMode && campaignId) {
        // Update existing campaign
        await crmService.updateEmailCampaign(campaignId, campaignData);
        finalCampaignId = campaignId; // Use the provided campaignId
        
        if (!finalCampaignId) {
          throw new Error('Campaign ID is required for updating recipients');
        }
        
        // Delete existing recipients and recreate them
        const existingRecipients = await crmService.getCampaignRecipients(campaignId);
        for (const recipient of existingRecipients) {
          try {
            await api.delete(`/crm/campaign-recipients/${recipient.id}`);
          } catch (err) {
            console.error('Error deleting recipient:', err);
          }
        }
      } else {
        // Create new campaign
        const campaign = await crmService.createEmailCampaign(campaignData);
        finalCampaignId = (campaign as any)?.id || (campaign as any)?.data?.id || '';
        
        if (!finalCampaignId) {
          throw new Error('Failed to create campaign: No campaign ID returned');
        }
      }

      // Create recipients
      const allRecipients: Array<{ campaign_id: string; contact_id?: string; email_address: string; type: 'to' | 'cc' | 'bcc' }> = [];

      // Process TO recipients
      for (const recipientId of toRecipients) {
        if (recipientId.startsWith('user:')) {
          const userId = recipientId.replace('user:', '');
          const user = users.find(u => u.id === userId);
          if (user?.email) {
            // Find contact record for this user by email
            // All recipients should have contact_id - users should have corresponding contact records
            const contact = contacts.find(c => c.email === user.email);
            if (contact) {
              allRecipients.push({
                campaign_id: finalCampaignId,
                contact_id: contact.id,
                email_address: contact.email,
                type: 'to'
              });
            } else {
              console.warn(`No contact record found for user ${user.email} - skipping recipient`);
            }
          }
        } else {
          const contact = contacts.find(c => c.id === recipientId);
          if (contact?.email) {
            allRecipients.push({
              campaign_id: finalCampaignId,
              contact_id: contact.id,
              email_address: contact.email,
              type: 'to'
            });
          }
        }
      }

      // Process CC recipients
      for (const recipientId of ccRecipients) {
        if (recipientId.startsWith('user:')) {
          const userId = recipientId.replace('user:', '');
          const user = users.find(u => u.id === userId);
          if (user?.email) {
            // Find contact record for this user by email
            // All recipients should have contact_id - users should have corresponding contact records
            const contact = contacts.find(c => c.email === user.email);
            if (contact) {
              allRecipients.push({
                campaign_id: finalCampaignId,
                contact_id: contact.id,
                email_address: contact.email,
                type: 'cc'
              });
            } else {
              console.warn(`No contact record found for user ${user.email} - skipping recipient`);
            }
          }
        } else {
          const contact = contacts.find(c => c.id === recipientId);
          if (contact?.email) {
            allRecipients.push({
              campaign_id: finalCampaignId,
              contact_id: contact.id,
              email_address: contact.email,
              type: 'cc'
            });
          }
        }
      }

      // Process BCC recipients
      for (const recipientId of bccRecipients) {
        if (recipientId.startsWith('user:')) {
          const userId = recipientId.replace('user:', '');
          const user = users.find(u => u.id === userId);
          if (user?.email) {
            // Find contact record for this user by email
            // All recipients should have contact_id - users should have corresponding contact records
            const contact = contacts.find(c => c.email === user.email);
            if (contact) {
              allRecipients.push({
                campaign_id: finalCampaignId,
                contact_id: contact.id,
                email_address: contact.email,
                type: 'bcc'
              });
            } else {
              console.warn(`No contact record found for user ${user.email} - skipping recipient`);
            }
          }
        } else {
          const contact = contacts.find(c => c.id === recipientId);
          if (contact?.email) {
            allRecipients.push({
              campaign_id: finalCampaignId,
              contact_id: contact.id,
              email_address: contact.email,
              type: 'bcc'
            });
          }
        }
      }

      // Create all recipients with recipient_type
      if (allRecipients.length > 0) {
        for (const recipient of allRecipients) {
          try {
            // Ensure we have valid data before sending
            if (!recipient.campaign_id || !recipient.email_address) {
              console.error('Invalid recipient data:', recipient);
              continue;
            }
            
            const recipientData: any = {
              campaign_id: recipient.campaign_id,
              email_address: recipient.email_address,
              recipient_type: recipient.type || 'to', // Default to 'to' if not specified
              status: 'pending'
            };
            
            // Only include contact_id if it exists (it's optional)
            if (recipient.contact_id) {
              recipientData.contact_id = recipient.contact_id;
            }
            
            await api.post('/crm/campaign-recipients', recipientData);
          } catch (err: any) {
            console.error('Error creating recipient:', err);
            // Log the actual validation errors if available
            if (err.response?.data?.errors) {
              console.error('Validation errors:', err.response.data.errors);
            }
          }
        }
      }

      // Update campaign with total recipients count
      await crmService.updateEmailCampaign(finalCampaignId, {
        total_recipients: allRecipients.length
      });

      // If no scheduled date, send immediately
      if (!scheduledDateTime) {
        try {
          await crmService.sendEmailCampaign(finalCampaignId);
        } catch (sendError: any) {
          console.error('Error sending campaign immediately:', sendError);
          // Don't fail the whole operation if immediate send fails
          // Campaign is saved as draft and can be sent later
        }
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      console.error('Error creating campaign:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCampaignName('');
    setSubject('');
    setEmailContent('');
    setSelectedTemplate('');
    setToRecipients([]);
    setCcRecipients([]);
    setBccRecipients([]);
    setScheduledDate('');
    setScheduledTime('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">
          {isEditMode ? 'Edit Email Campaign' : 'Send/Schedule Email'}
        </h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Campaign Name */}
          <div>
            <input
              type="text"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="Campaign Name *"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          {/* Template Selection */}
          <div>
            <select
              value={selectedTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">Email Template (optional)</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          {/* Recipients Section - TO, CC, BCC with autocomplete */}
          <div className="grid grid-cols-3 gap-4">
            {/* TO Recipients */}
            <div>
              <Select
                isMulti
                options={recipientOptions}
                value={getSelectedRecipients('to')}
                onChange={(selected) => handleRecipientSelectChange('to', selected)}
                placeholder="TO * (Type to search...)"
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#d1d5db',
                    minHeight: '38px',
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#e5e7eb',
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#374151',
                  }),
                }}
              />
            </div>

            {/* CC Recipients */}
            <div>
              <Select
                isMulti
                options={recipientOptions}
                value={getSelectedRecipients('cc')}
                onChange={(selected) => handleRecipientSelectChange('cc', selected)}
                placeholder="CC (Type to search...)"
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#d1d5db',
                    minHeight: '38px',
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#e5e7eb',
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#374151',
                  }),
                }}
              />
            </div>

            {/* BCC Recipients */}
            <div>
              <Select
                isMulti
                options={recipientOptions}
                value={getSelectedRecipients('bcc')}
                onChange={(selected) => handleRecipientSelectChange('bcc', selected)}
                placeholder="BCC (Type to search...)"
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#d1d5db',
                    minHeight: '38px',
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#e5e7eb',
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#374151',
                  }),
                }}
              />
            </div>
          </div>

          {/* Email Subject */}
          <div>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject *"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          {/* Message */}
          <div>
            <RichTextEditor
              value={emailContent}
              onChange={setEmailContent}
              placeholder="Message * (Enter email message - HTML supported)"
              className="mb-2"
            />
          </div>

          {/* Schedule Date, Time, and Timezone */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                placeholder="Schedule Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                placeholder="Schedule Time"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={!scheduledDate}
              />
            </div>
            <div>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={!scheduledDate}
              >
                <option value="">Timezone</option>
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Asia/Shanghai">Shanghai (CST)</option>
                <option value="Asia/Kolkata">Mumbai (IST)</option>
                <option value="Australia/Sydney">Sydney (AEDT)</option>
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Sending...') : (isEditMode ? 'Update Campaign' : 'Send Email')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

